/**
 * @file Renders the keyword search that finds an investment to jump to.
 */
import { useEffect, useRef, useState, type ComponentProps } from "react";
import { AutoComplete, Input, Space, Typography } from "antd";
import type { AutoCompleteProps, InputRef } from "antd";

import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import { getCurrencyValue } from "@components/Amount";
import { INDIVIDUAL, JOINT } from "@api/services/investments/shapes/ProfileTypeEnum";
import { searchInvestmentsOperation } from "@api/services/investments";

const OPTIONS_WIDTH = 600;
const MIN_KEYWORD_SIZE = 3;

const LABEL_PLACEHOLDER = "Search by keyword...";

type Investor = {
  givenName?: string;
  familyName?: string;
};

type SearchResultItem = {
  id: string;
  name: string;
  investor: Investor;
  isFunded: boolean;
  partition: string;
  projectId: string;
  profileType: string;
  totalAmount: number;
};

type SearchOutput = {
  data: SearchResultItem[];
};

type Options = AutoCompleteProps["options"];

type InputSearchProps = ComponentProps<typeof Input.Search>;

type Props = {
  onSelect: (
    organizationId: string,
    projectId: string,
    investmentId: string,
  ) => Promise<void> | void;
};

/**
 * Renders the keyword search that finds an investment to jump to.
 */
const GoToInvestmentSearch = ({ onSelect }: Props) => {
  const [ options, setOptions ] = useState<Options>([]);
  const [ isLoading, setIsLoading ] = useState(false);
  const inputRef = useRef<InputRef>(null);

  const { request, identity } = useAppContext() as AppContext;

  /**
   * Looks up an organization's name by its ID among the current identity's organizations.
   */
  const getOrganizationName = (id: string) =>
    identity?.organizations?.find((organization) => organization.id === id)?.name;

  /**
   * Builds a search result's display name, prefixed with the investor name for a non-individual profile.
   */
  const getInvestmentName = (searchResultItem: SearchResultItem) => {
    const { investor, name, profileType } = searchResultItem;

    const isIndividual = [ JOINT, INDIVIDUAL ].includes(profileType);

    if (isIndividual) {
      return name;
    }

    const { givenName, familyName } = investor;
    const investorFullName = [ givenName, familyName ].join(" ");

    return `${investorFullName} - ${name}`;
  };

  /**
   * Renders a search result's option label with its investment name, amount and organization.
   */
  const renderLabel = (searchResultItem: SearchResultItem) => {
    const { partition, totalAmount, isFunded } = searchResultItem;

    const [ amount ] = getCurrencyValue(totalAmount).split(".");
    const investmentName = getInvestmentName(searchResultItem);
    const organizationName = getOrganizationName(partition);

    const titleItems = [ investmentName, amount ];

    if (isFunded) {
      titleItems.push("Funded");
    }

    const title = titleItems.join(" / ");

    return (
      <Space direction="vertical" size={0}>
        <Typography.Text>{title}</Typography.Text>
        <Typography.Text type="secondary">{organizationName}</Typography.Text>
      </Space>
    );
  };

  /**
   * Replaces the search options with the labeled and valued search results.
   */
  const updateOptions = (searchResultItems: SearchResultItem[]) => {
    const updatedOptions = searchResultItems
      .map((item) => ({
        label: renderLabel(item),
        value: `${item.partition}-${item.projectId}-${item.id}`,
      }));

    setOptions(updatedOptions);
  };

  /**
   * Searches for investments by keyword and fills the options with the results.
   */
  const onSearch = (keyword = "") => {
    const shouldSkip = keyword.length < MIN_KEYWORD_SIZE;

    if (shouldSkip) {
      return;
    }

    setIsLoading(true);

    request<SearchOutput>(searchInvestmentsOperation, { keyword })
      .then(({ data }) => updateOptions(data))
      .then(() => setIsLoading(false));
  };

  /**
   * Clears the search options.
   */
  const onClear = () => {
    setOptions([]);
  };

  /**
   * Clears the search options once the keyword drops below the minimum search length.
   */
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value = "" } = event.target;
    const shouldClearOptions = value.length < MIN_KEYWORD_SIZE;

    if (shouldClearOptions) {
      onClear();
    }
  };

  /**
   * Splits the selected option's value into its organization, project and investment IDs and selects it.
   */
  const onItemSelect = (value: string) => {
    const [ organizationId, projectId, investmentId ] = value.split("-");

    return onSelect(organizationId, projectId, investmentId);
  };

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  const autoCompleteProps: AutoCompleteProps = {
    open: true,
    style: { width: "100%" },
    onSelect: onItemSelect,
    popupMatchSelectWidth: OPTIONS_WIDTH,
    options,
  };

  const inputSearchProps: InputSearchProps = {
    ref: inputRef,
    autoFocus: true,
    loading: isLoading,
    allowClear: true,
    placeholder: LABEL_PLACEHOLDER,
    onClear,
    onChange,
    onSearch,
  };

  return (
    <AutoComplete {...autoCompleteProps}>
      <Input.Search {...inputSearchProps} />
    </AutoComplete>
  );
};

export default GoToInvestmentSearch;
